import { MemberType } from '../../../types';
import { EMAIL_VALID_REGEX } from '../consts';

export type ParsedUserData = {
  itemIndex: string;
  email: string;
  fullname: string;
  externalId: string;
  role: string;
  userGroups: string[];
};

export type InvalidParsedUserData = ParsedUserData & {
  emailWarn?: string;
  roleWarn?: string;
};

const parseMemberRowData = (
  rows: string[],
  memberType: MemberType
): { parsedData: ParsedUserData[]; invalidData: InvalidParsedUserData[] } => {
  const parsedData: ParsedUserData[] = [];
  const invalidData: InvalidParsedUserData[] = [];
  for (let i = 1; i < rows.length; i++) {
    // starts from row 1, since row 0 is the table heading

    const [email, fullname, externalId, ...extraData] = rows[i].split(',');
    const newAccountCheck = [
      {
        field: 'email',
        isValid: EMAIL_VALID_REGEX.test(email),
        warnMessage: 'Invalid email format',
      },
    ];
    const errorMessage: {
      emailWarn?: string;
      roleWarn?: string;
    } = {};

    if (memberType === 'team members') {
      const [role, ...userGroups] = extraData;

      // Handle data validation
      if (email) {
        // only process rows that has email
        newAccountCheck.push({
          field: 'role',
          isValid: ['admin', 'staff'].includes(role),
          warnMessage: 'Role does not exist',
        });

        const newAccountIsValid = newAccountCheck.reduce((prev, current) => {
          if (!current.isValid) {
            if (current.field === 'email')
              errorMessage.emailWarn = current.warnMessage;
            else if (current.field === 'role')
              errorMessage.roleWarn = current.warnMessage;
          }
          return prev && current.isValid;
        }, true);

        const accountRow: ParsedUserData = {
          itemIndex: `${i + 1}`,
          email,
          fullname,
          externalId,
          role,
          userGroups,
        };

        if (newAccountIsValid) {
          parsedData.push(accountRow);
        } else {
          invalidData.push({ ...accountRow, ...errorMessage });
        }
      }
    } else if (memberType === 'learners') {
      const userGroups = extraData;

      // Handle data validation
      if (email) {
        // only process rows that has email

        const newAccountIsValid = newAccountCheck.reduce((prev, current) => {
          if (!current.isValid) {
            if (current.field === 'email')
              errorMessage.emailWarn = current.warnMessage;
          }
          return prev && current.isValid;
        }, true);

        const accountRow: ParsedUserData = {
          itemIndex: `${i + 1}`,
          email,
          fullname,
          externalId,
          role: 'user',
          userGroups,
        };

        if (newAccountIsValid) {
          parsedData.push(accountRow);
        } else {
          invalidData.push({ ...accountRow, ...errorMessage });
        }
      }
    }
  }
  return { parsedData, invalidData };
};

export const handleMembersCsvUpload = (
  memberType: MemberType,
  file: File | null
): Promise<{
  parsedData: ParsedUserData[];
  invalidData: InvalidParsedUserData[];
}> => {
  return new Promise((resolve, reject) => {
    if (file) {
      const reader = new FileReader();
      reader.addEventListener('error', (error) => reject(error));
      reader.addEventListener('load', (e) => {
        const csvdata = e.target?.result as string;

        const rows = csvdata?.split('\n');
        const { parsedData, invalidData } = parseMemberRowData(
          rows,
          memberType
        );
        resolve({ parsedData, invalidData });
      });
      reader.readAsBinaryString(file);
    }
  });
};
